// Vendors
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  acceptConsent,
  logoutAndSignInDifferent,
  clearUserDetails,
} from "../../../reducers/auth/authActions";

// Helpers
import { clearAuthSession } from '../../../reducers/auth/authAuthorizeHelpers';
import { 
  selectAppName, 
  selectAppUrl,
  selectScope,
} from '../../../reducers/auth/authSelectors';

// Styles
import './grant-permission.scss';

// Components
import {
  TxButton,
  TxButtonMode,
  TxLoadingText,
} from 'texkit-ui';
import { Trans } from '../../../components/Trans';
import Icon from '../../../components/Icon/Icon';

const GrantPermission = () => {
  const dispatch = useDispatch();
  const appName = useSelector(selectAppName);
  const appUrl = useSelector(selectAppUrl);
  const scope = useSelector(selectScope);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Check if scope includes dlIdInfo and dobInfo
  const showDLID = scope.includes('dlIdInfo');
  const showDOB = scope.includes('dobInfo');

  const handleCancel = async () => {
    if (isCancelling || isSubmitting) return;
    setIsCancelling(true);
    const logoutResult = await dispatch(logoutAndSignInDifferent());
    
    if (logoutResult?.success && appUrl) {
      dispatch(clearUserDetails());
      clearAuthSession();
      window.location.href = appUrl;
    } else {
      setIsCancelling(false);
    }
  };

  const handleContinue = async () => {
    if (isSubmitting || isCancelling) return;
    
    setIsSubmitting(true);
    const result = await dispatch(acceptConsent());
    if (result === false) {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="step grant-permission-step">
      <h1 className="step-title grant-permission-title">
        <Trans file="auth" id="grantAccessTitle" args={{ customerAppName: appName }} />
      </h1>
      <p className="step-subtitle grant-permission-subtitle">
        <Trans file="auth" id="grantAccessSubtitlePrefix" />
        <strong>{appName}</strong>
        <Trans file="auth" id="grantAccessSubtitleSuffix" />
      </p>

      <div className="grant-permission-profile">
        <div className="grant-permission-item">
          <Icon name="user" className="grant-permission-item-icon" />
          <span className="grant-permission-item-text">
            <Trans file="auth" id="grantAccessFirstLastName" />
          </span>
        </div>

        <div className="grant-permission-item">
          <Icon name="email" className="grant-permission-item-icon" />
          <span className="grant-permission-item-text">
            <Trans file="auth" id="grantAccessEmailAddress" />
          </span>
        </div>

        <div className="grant-permission-item">
          <Icon name="phone" className="grant-permission-item-icon" />
          <span className="grant-permission-item-text">
            <Trans file="auth" id="grantAccessPhoneNumber" />
          </span>
        </div>

        <div className="grant-permission-item">
          <Icon name="home" className="grant-permission-item-icon" />
          <span className="grant-permission-item-text">
            <Trans file="auth" id="grantAccessAddress" />
          </span>
        </div>

        {showDLID && (
          <div className="grant-permission-item">
            <Icon name="studentCard" className="grant-permission-item-icon" />
            <span className="grant-permission-item-text">
              <Trans file="auth" id="grantAccessDLOrID" />
            </span>
          </div>
        )}

        {showDOB && (
          <div className="grant-permission-item">
            <Icon name="calendar" className="grant-permission-item-icon" />
            <span className="grant-permission-item-text">
              <Trans file="auth" id="grantAccessDOB" />
            </span>
          </div>
        )}
      </div>

      <div className="grant-permission-footer">
        <div className="grant-permission-actions">
          <TxButton
            className="btn grant-permission-cancel-btn"
            type="button"
            onClick={handleCancel}
            mode={TxButtonMode.Secondary}
            disabled={isSubmitting || isCancelling}
            aria-busy={isCancelling}
          >
            {isCancelling ? (
              <TxLoadingText>
                <Trans file="auth" id="cancel" />
              </TxLoadingText>
            ) : (
              <Trans file="auth" id="cancel" />
            )}
          </TxButton>

          <TxButton
            className="grant-permission-continue-btn"
            mode={TxButtonMode.Primary}
            type="button"
            onClick={handleContinue}
            disabled={isSubmitting || isCancelling}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <TxLoadingText>
                <Trans file="auth" id="accept" />
              </TxLoadingText>
            ) : (
              <Trans file="auth" id="accept" />
            )}
          </TxButton>
        </div>
      </div>
    </div>
  );
};

export default GrantPermission;
